import { keepPreviousData, QueryClient, useQuery } from '@tanstack/react-query';
import type { AuditEntryDTO, ConversationDetailDTO, ConversationDTO, MeDTO, MemoryNoteDTO, ViewDataDTO, ViewDTO } from '@sam/shared';
import { api } from './api';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10_000 } },
});

export const keys = {
  me: ['me'] as const,
  conversations: ['conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  memory: ['memory'] as const,
  audit: ['audit'] as const,
  views: ['views'] as const,
  view: (id: string) => ['view', id] as const,
  viewData: (id: string) => ['view-data', id] as const,
};

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => api<MeDTO>('GET', '/me'), refetchInterval: 30_000, staleTime: 5_000 });

// Obnovuje se po minutě, ať se výsledky naplánovaných úloh objeví v menu i bez akce uživatele.
export const useConversations = () =>
  useQuery({
    queryKey: keys.conversations,
    queryFn: async () => (await api<{ conversations: ConversationDTO[] }>('GET', '/conversations')).conversations,
    refetchInterval: 60_000,
  });

export const useConversation = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.conversation(id), queryFn: () => api<ConversationDetailDTO>('GET', `/conversations/${id}`), staleTime: 0, enabled });

export const useViews = () =>
  useQuery({ queryKey: keys.views, queryFn: async () => (await api<{ views: ViewDTO[] }>('GET', '/views')).views });

export const useView = (id: string) =>
  useQuery({ queryKey: keys.view(id), queryFn: async () => (await api<{ view: ViewDTO }>('GET', `/views/${id}`)).view });

/** Živá data pohledu přes bránu. Zamítnutí není chyba dotazu, ale stav (ok: false). */
export const useViewData = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.viewData(id),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch(`/api/views/${id}/data`, { credentials: 'same-origin' });
      return (await res.json()) as ViewDataDTO;
    },
  });

export const useMemory = (enabled: boolean) =>
  useQuery({ queryKey: keys.memory, enabled, queryFn: async () => (await api<{ notes: MemoryNoteDTO[] }>('GET', '/memory')).notes, staleTime: 0 });

/** Stránka záznamu přístupů; při přepnutí stránky zůstane vidět předchozí, dokud nepřijde nová. */
export const useAudit = (enabled: boolean, page: number, pageSize = 25) =>
  useQuery({
    queryKey: [...keys.audit, page, pageSize],
    enabled,
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: () => api<{ entries: AuditEntryDTO[]; total: number; page: number; pageSize: number }>('GET', `/audit?page=${page}&pageSize=${pageSize}`),
  });
